'use client';

import { MouseEvent, useEffect, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useStoreMap, useUnit } from 'effector-react';
import classNames from 'classnames';
import { $isShownContextMenu } from '@nx-frontend/shared/ui/context-menu';
import { EsignButton } from '@nx-frontend/shared/ui/esign-button';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { ListItemIcon, ListItemSize, ListItemTimeAgo } from '@nx-frontend/shared/ui/list-item';
import { UserPreview } from '@nx-frontend/shared/ui/user-preview';
import { getDocByAttachmentIdParams } from '@nx-frontend/shared/store/esign';
import { getFileByIdParams } from '@nx-frontend/shared/store/feature-files';
import { getUserByIdParams } from '@nx-frontend/shared/store/users-store';
import { getBgClassNames, getBorderClassNames, getFillClassNames, getTextClassNames } from '@nx-frontend/shared/themes';
import {
  BorderPosition,
  BucketFile,
  PortalBgType,
  PortalBorderType,
  PortalClassState,
  PortalStyleConfig,
  PortalTextType,
  ThemeColorSchemas,
} from '@nx-frontend/shared/types';
import { ModalState } from '@nx-frontend/shared/types';
import { getPermission, getUserDisplayName } from '@nx-frontend/shared/utils';
import { getIconByFileType } from '../helpers';
import { useOpenFileMenu } from '../hooks/useOpenFileMenu';
import { useShowFileInfo } from '../hooks/useShowFileInfo';
import { FileManagerHandlers } from '../model';
import { FileDestination } from './file-destination';

export enum FileListItemColumn {
  All = 'all',
  NAME = 'name',
  ESIGN = 'esign',
  TYPE = 'type',
  SIZE = 'size',
  USER = 'user',
  CREATED = 'created',
  MENU = 'MENU',
}

interface FileListItemProps {
  themeColors: ThemeColorSchemas;
  themeStyle: PortalStyleConfig;
  id: string;
  fileOpenHost: string;
  userId?: number;
  hasBorder?: boolean;
  visibleColumns?: FileListItemColumn[];
  columnWidths?: {
    name?: string;
    type?: string;
    user?: string;
    size?: string;
    created?: string;
    menu?: string;
  };
  orgRole?: string;
  itemCustomClass?: string;
  showCheckForNewFiles?: boolean;
  hideDestination?: boolean;
  showMenuOnHover?: boolean;
  openFileLocationEnabled?: boolean;
  canCreateEsign: boolean;
  canSubmitEsign: boolean;
  isCustomizerMode?: boolean;
  showNewFileDot?: boolean;
  canEditOtherUsersFile?: boolean;
  handlers?: FileManagerHandlers;
  personalizationUserId?: number | null;
  deleteDisabled?: boolean;
  renameDisabled?: boolean;
}

export interface ShowFileParams {
  item: BucketFile;
  themeColors: ThemeColorSchemas;
  themeStyle: PortalStyleConfig;
  orgRole?: string;
  userId?: number;
  openFileLocationEnabled: boolean;
  canEditOtherUsersFile: boolean;
  openModal: (modalState: ModalState) => void;
  openImageViewer?: (image: { src: string; title?: string; description?: string }) => void;
}

export const FileListItem: React.FC<FileListItemProps> = ({
  themeColors,
  themeStyle,
  id,
  fileOpenHost,
  userId,
  visibleColumns = FileListItemColumn.All,
  columnWidths = {},
  orgRole,
  hasBorder = true,
  itemCustomClass,
  showCheckForNewFiles,
  hideDestination,
  showMenuOnHover,
  openFileLocationEnabled = true,
  canEditOtherUsersFile = false,
  canCreateEsign = false,
  canSubmitEsign = false,
  isCustomizerMode = false,
  personalizationUserId = null,
  renameDisabled,
  deleteDisabled,
}) => {
  const item = useStoreMap(getFileByIdParams(id));
  const user = useStoreMap(getUserByIdParams(item.userId));
  const esignDoc = useStoreMap(getDocByAttachmentIdParams(id));
  const [isShownContextMenu] = useUnit([$isShownContextMenu]);
  const [menuOpened, setMenuOpened] = useState(false);

  const t = useTranslations();

  const hasPermission = item?.permissions
    ? getPermission({ userId: personalizationUserId ?? userId, permissions: item.permissions })
    : true;
  const openFileMenu = useOpenFileMenu();
  const showFileInfo = useShowFileInfo();

  useEffect(() => {
    if (!isShownContextMenu && menuOpened) {
      setMenuOpened(false);
    }
  }, [isShownContextMenu, menuOpened]);

  const { filename, format } = useMemo(() => {
    const filenameSplited = item.filename.split('.');
    const format = filenameSplited.length > 1 ? filenameSplited[filenameSplited.length - 1] : undefined;
    if (filenameSplited.length > 1) {
      filenameSplited.pop();
    }
    const filename = filenameSplited.join('.');
    return { filename, format };
  }, [item]);

  if (!item || !hasPermission) {
    return;
  }
  const icon = getIconByFileType(item.type);
  const userName = user ? getUserDisplayName(user, userId ?? 0) : 'Anonymous User';
  const destination = item.breadcrumbs ? item.breadcrumbs[0] : undefined;
  const destinationUrl = destination?.url;
  let destinationName = destination?.name ?? (destination?.url && !destination.name ? 'Homepage' : undefined);
  if (destination?.prefixLocKey) {
    destinationName = t(destination.prefixLocKey) + ' • ' + destinationName;
  }

  const shouldDisplayAllFields = visibleColumns?.includes(FileListItemColumn.All);

  const handleItemClick = (event: MouseEvent) => {
    event.preventDefault();
    const target = event.target as Element;
    if (target.nodeName.toLowerCase() === 'a') {
      return;
    }

    showFileInfo({
      item,
      orgRole,
      userId,
      themeColors,
      themeStyle,
      fileOpenHost,
      openFileLocationEnabled,
      canEditOtherUsersFile,
      renameDisabled,
      deleteDisabled,
    });
  };

  const handleMenuClick = (item: BucketFile, event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setMenuOpened(true);
    openFileMenu({
      item,
      userId,
      openFileLocationEnabled,
      themeColors,
      themeStyle,
      fileOpenHost,
      event,
      canEditOtherUsersFile,
      canCreateEsign: 'pdf' === item.format ? canCreateEsign : false,
      isCustomizerMode,
      esignDoc,
      renameDisabled,
      deleteDisabled,
    });
  };

  const showCheck = item.isNew && showCheckForNewFiles;
  const borderClasses = hasBorder
    ? getBorderClassNames(themeColors, themeStyle, PortalBorderType.SURFACE, { position: BorderPosition.BOTTOM })
    : [];

  return (
    <div
      className={classNames(
        'flex items-center justify-between py-3 text-[13px] sm:px-4 font-normal w-full cursor-pointer group/file-item',
        getBgClassNames(themeColors, PortalBgType.SURFACE_INNER_HOVER, [PortalClassState.HOVER]),
        borderClasses,
        itemCustomClass
      )}
      onClick={handleItemClick}
    >
      {(shouldDisplayAllFields || visibleColumns.includes(FileListItemColumn.NAME)) && (
        <div
          className={classNames(
            'min-w-0 flex items-center justify-start gap-3 w-[64%] sm:w-[35%] relative',
            getFillClassNames(themeColors, PortalBgType.FILL_SURFACE)
          )}
          style={columnWidths.name ? { width: columnWidths.name } : undefined}
        >
          {item.isNew && <div className="absolute left-8 top-[5px] h-2 w-2 bg-green-500 rounded-full"></div>}
          <ListItemIcon themeColors={themeColors} themeStyle={themeStyle} icon={icon} />
          <div className="flex flex-col font-normal items-start overflow-hidden pr-2">
            <div
              className={classNames(
                'flex pr-2 text-[13px] leading-5 w-full text-ellipsis overflow-hidden whitespace-nowrap',
                getTextClassNames(themeColors, PortalTextType.SURFACE)
              )}
              title={filename + '.' + format}
            >
              <span
                className={classNames(
                  'overflow-hidden overflow-ellipsis whitespace-nowrap',
                  ...getTextClassNames(themeColors, PortalTextType.SURFACE)
                )}
              >
                {filename}
              </span>
              {format && <span>.{format}</span>}
            </div>

            {destinationName && destinationUrl && !hideDestination && (
              <FileDestination
                type={destination.type}
                name={destinationName}
                url={destinationUrl}
                themeColors={themeColors}
              />
            )}
          </div>
        </div>
      )}
      {(shouldDisplayAllFields || visibleColumns.includes(FileListItemColumn.TYPE)) && (
        <div
          className="min-w-0 hidden sm:block uppercase text-[10px] w-[13%]"
          style={columnWidths.type ? { width: columnWidths.type } : undefined}
        >
          {item.type}
        </div>
      )}
      {(shouldDisplayAllFields ||
        (visibleColumns.includes(FileListItemColumn.ESIGN) && (canCreateEsign || canSubmitEsign))) && (
        <div
          className={classNames(
            `min-w-0 sm:flex items-center justify-start ${
              canCreateEsign || canSubmitEsign ? 'w-[52%] sm:w-[18%]' : 'hidden w-[21%]'
            }`,
            ...getTextClassNames(themeColors, PortalTextType.SURFACE)
          )}
          style={columnWidths.user ? { width: columnWidths.user } : undefined}
        >
          <EsignButton
            isPdf={'pdf' === item.format}
            userId={userId}
            canCreateEsign={canCreateEsign}
            canSubmitEsign={canSubmitEsign}
            isCustomizerMode={isCustomizerMode}
            esignDoc={esignDoc}
            file={item}
            themeColors={themeColors}
            themeStyle={themeStyle}
          />
        </div>
      )}
      {(shouldDisplayAllFields || visibleColumns.includes(FileListItemColumn.USER)) && (
        <div
          className={classNames(
            'min-w-0 hidden sm:flex items-center justify-start w-[21%]',
            ...getTextClassNames(themeColors, PortalTextType.SURFACE)
          )}
          style={columnWidths.user ? { width: columnWidths.user } : undefined}
        >
          <UserPreview name={userName} avatarUrl={user?.avatar} />
        </div>
      )}
      {(shouldDisplayAllFields || visibleColumns.includes(FileListItemColumn.SIZE)) && (
        <div
          className="min-w-0 hidden sm:block text-gray-400 overflow-hidden overflow-ellipsis whitespace-nowrap w-[15%]"
          style={columnWidths.size ? { width: columnWidths.size } : undefined}
        >
          <ListItemSize size={item.size} />
        </div>
      )}
      {(shouldDisplayAllFields || visibleColumns.includes(FileListItemColumn.CREATED)) && (
        <div
          className={`contents min-w-0 text-gray-400 overflow-hidden overflow-ellipsis whitespace-nowrap px-0.5 w-[30%] sm:w-[10%] ${
            canCreateEsign || canSubmitEsign ? 'hidden lg:block' : 'sm:block'
          }`}
          style={columnWidths.created ? { width: columnWidths.created } : undefined}
        >
          <ListItemTimeAgo timestamp={item.createdAt} />
        </div>
      )}
      {(shouldDisplayAllFields || visibleColumns.includes(FileListItemColumn.MENU)) && (
        <div
          className="min-w-0 w-[6%] flex justify-end"
          style={columnWidths.menu ? { width: columnWidths.menu } : undefined}
        >
          <div
            className={classNames(
              'rounded-md cursor-pointer p-1',
              ...getTextClassNames(themeColors, PortalTextType.SURFACE),
              ...getBgClassNames(themeColors, PortalBgType.SURFACE_INNER_HOVER_2, [PortalClassState.HOVER]),
              {
                'hidden group-hover/file-item:flex': showMenuOnHover,
                '!flex': menuOpened,
              }
            )}
            onClick={(event) => handleMenuClick(item, event)}
          >
            <Icon icon={IconsMap.MORE_HORIZONTAL} className="w-4 h-4" />
          </div>
        </div>
      )}
      {showCheck && (
        <div className="w-[14px] h-[14px] ml-1 rounded-full bg-green-500 flex items-center justify-center">
          <Icon icon={IconsMap.CHECK_BULLET} className="w-3 h-3 text-white" />
        </div>
      )}
    </div>
  );
};
